import network from '@/services/network';
import { ErrorResponse } from '@/utils';

/**
 * Organigramm / Ansprechpartnerliste (Auftrag Andreas Bitner 21.09.2026).
 *
 * Kanonische Scope-Quelle: `docs/tasks/vertriebsservice-portal.md` §3.2.
 *
 * 🔥 DIE LISTE KOMMT VOM SERVER, NICHT AUS DEM CODE. Eine
 * Ansprechpartnerliste ist die am schnellsten veraltende Seite eines
 * Unternehmens — stuende sie hier als Konstante, kostete jeder Eintritt,
 * Austritt und Rollenwechsel einen Deploy, und binnen Wochen stuenden im
 * Vertrieb Namen, die es nicht mehr gibt. Dieselbe Begruendung wie bei den
 * Vertriebsservice-Themen.
 *
 * `schluessel` IST DIE IDENTITAET, alles andere ist Anzeige. Er wird
 * serverseitig vergeben und ist kein Formularfeld — nie darauf schreiben,
 * nie `label`/`name` vergleichen.
 */
export interface OrganigrammEintrag {
  id: number;
  schluessel: string;
  name: string;
  funktion: string;
  /**
   * Untergruppe innerhalb des Bereichs, z. B. der Vertriebsdirektor.
   *
   * 🔥 EIGENES FELD, KEIN REGEX AUF `aufgaben`. Die Zuordnung stand frueher
   * im Fließtext („Team Aksoy · Vertriebsdirektor: Arian Haijat"); sie dort
   * herauszuparsen hätte auf einen editierbaren Anzeigetext geschlüsselt und
   * wäre beim ersten Pflegevorgang still kaputtgegangen.
   */
  gruppe: string | null;
  /** Sammeladressen haben keine Telefonnummer, der Vorstand gibt bewusst keine heraus. */
  email: string | null;
  telefon: string | null;
  aufgaben: string | null;
  sortierung: number;
  is_active: boolean;
}

export interface OrganigrammBereich {
  id: number;
  schluessel: string;
  label: string;
  beschreibung: string | null;
  sortierung: number;
  is_active: boolean;
  eintraege: OrganigrammEintrag[];
}

export interface OrganigrammAntwort {
  /**
   * Reine ANZEIGE-Auskunft fuer die Pflege-Schaltflaeche — kein Schutz. Der
   * sitzt in den Policies; ein Frontend, das das Flag ignoriert, kommt am
   * Server trotzdem nicht vorbei.
   */
  darf_pflegen: boolean;
  bereiche: OrganigrammBereich[];
}

/** Felder, die die Pflege-Oberflaeche schreiben darf. `schluessel` ist bewusst nicht dabei. */
export interface EintragEingabe {
  bereich_id: number;
  name: string;
  funktion: string;
  gruppe: string | null;
  email: string | null;
  telefon: string | null;
  aufgaben: string | null;
  sortierung?: number;
  is_active?: boolean;
}

export interface BereichEingabe {
  label: string;
  beschreibung: string | null;
  sortierung?: number;
  is_active?: boolean;
}

/**
 * `pflege = true` liefert zusaetzlich die inaktiven Zeilen und ist
 * berechtigungspflichtig (403 ohne Pflegerecht).
 */
const liste = async (pflege = false): Promise<OrganigrammAntwort> => {
  try {
    const { data } = await network.get<{ data: OrganigrammAntwort }>('/organigramm', {
      params: pflege ? { pflege: 1 } : undefined,
    });
    return data.data;
  } catch (error) { throw new ErrorResponse(error); }
};

const bereichAnlegen = async (eingabe: BereichEingabe): Promise<number> => {
  try {
    const { data } = await network.post<{ data: { id: number } }>('/organigramm/bereiche', eingabe);
    return data.data.id;
  } catch (error) { throw new ErrorResponse(error); }
};

const bereichAendern = async (id: number, eingabe: Partial<BereichEingabe>): Promise<void> => {
  try {
    await network.put(`/organigramm/bereiche/${id}`, eingabe);
  } catch (error) { throw new ErrorResponse(error); }
};

const bereichLoeschen = async (id: number): Promise<void> => {
  try {
    await network.delete(`/organigramm/bereiche/${id}`);
  } catch (error) { throw new ErrorResponse(error); }
};

const eintragAnlegen = async (eingabe: EintragEingabe): Promise<number> => {
  try {
    const { data } = await network.post<{ data: { id: number } }>('/organigramm/eintraege', eingabe);
    return data.data.id;
  } catch (error) { throw new ErrorResponse(error); }
};

const eintragAendern = async (id: number, eingabe: Partial<EintragEingabe>): Promise<void> => {
  try {
    await network.put(`/organigramm/eintraege/${id}`, eingabe);
  } catch (error) { throw new ErrorResponse(error); }
};

const eintragLoeschen = async (id: number): Promise<void> => {
  try {
    await network.delete(`/organigramm/eintraege/${id}`);
  } catch (error) { throw new ErrorResponse(error); }
};

const organigrammApi = {
  liste,
  bereichAnlegen,
  bereichAendern,
  bereichLoeschen,
  eintragAnlegen,
  eintragAendern,
  eintragLoeschen,
};

export default organigrammApi;
